// Part groups (migration 039, shared/part-groups.ts), spread into `mutations` (shared/mutations.ts): grouping parts that
// differ by colour or model, adding a member like an existing one, and copying a shared file (the die cut) into every
// member's spec. part_group_id and group_values are set only here, never by item.update. "part_group.set_all" (every
// member's fields at once) is in shared/mutations.ts, beside the item fields it takes. Rules and implementations:
// server/src/mutations/part-groups.ts.
import { z } from "zod";
import { custom, STAFF, update } from "./mutation-kit.ts";
import { MAX_AXES } from "./part-groups.ts";
import { SPEC_FILE_KINDS } from "./specs.ts";

const axis = z.string().trim().min(1, "Name what the parts vary by").max(60);
/** What members differ by, in grid order: ["Connection type", "Color"]. */
export const variesBy = z
  .array(axis)
  .min(1, "Say what the parts vary by")
  .max(MAX_AXES, `Up to ${MAX_AXES} things a group varies by`)
  .refine((a) => new Set(a.map((x) => x.toLowerCase())).size === a.length, "Each thing it varies by once");
/** A member's values, one per axis: {"Connection type": "Wireless", "Color": "Atomic Purple"}. */
export const groupValues = z.record(axis, z.string().trim().min(1, "Give each part a value").max(100));
const member = z.object({ item_id: z.uuid(), values: groupValues });

export const partGroupFields = z.object({
  name: z.string().trim().min(1, "Name the group").max(200),
  /** What members' SKUs start with (GCU2-SLV-), for a new member's SKU. */
  sku_prefix: z.string().trim().toUpperCase().max(64),
  /** Spec file kinds every member carries the same file of (the die cut, the mould's STEP). */
  shared_files: z
    .array(z.enum(SPEC_FILE_KINDS))
    .max(SPEC_FILE_KINDS.length)
    .transform((k) => [...new Set(k)]),
  notes: z.string().trim().max(5000),
});

export const partGroupMutations = {
  /**
   * A new group (`id` made by the browser) with its members and their values, all at once. Parts in another group move
   * only with `move` (an emptied group goes). Returns { id, joined, moved: [{ sku, from }] }.
   */
  "part_group.create": custom(
    partGroupFields
      .partial()
      .required({ name: true })
      .extend({ id: z.uuid(), varies_by: variesBy, members: z.array(member).min(1, "Pick the parts that go in it").max(500), move: z.boolean().optional() }),
    STAFF,
  ),
  "part_group.update": update("part_group", partGroupFields, STAFF),
  /**
   * The group's members and their values, the whole list (parts left out leave the group), and what it varies by when
   * that changes. Returns { joined, left, moved: [{ sku, from }] }.
   */
  "part_group.set_members": custom(
    z.object({
      id: z.uuid(),
      varies_by: variesBy.optional(),
      members: z.array(member).min(1, "A group needs a part: dissolve it instead").max(500),
      move: z.boolean().optional(),
    }),
    STAFF,
  ),
  /**
   * A new member like `from_item_id` (`id` made by the browser): its fields, vendor links (no vendor SKU or name: those
   * are the other colour's), spec text and the group's shared files; never its artwork, stock or plan.
   * Returns { id, sku, links, files }.
   */
  "part_group.add_member": custom(
    z.object({
      id: z.uuid(),
      from_item_id: z.uuid(),
      sku: z.string().trim().toUpperCase().min(1, "Give it a SKU").max(64),
      name: z.string().trim().min(1, "Name it").max(200),
      values: groupValues,
    }),
    STAFF,
  ),
  /** Takes a part out of its group (the last one out dissolves it). Returns { left, dissolved }. */
  "part_group.leave": custom(z.object({ item_id: z.uuid() }), STAFF),
  /** Dissolves a group: its parts stay, each simply in no group. Returns { members }. */
  "part_group.remove": custom(z.object({ id: z.uuid() }), STAFF),
  /**
   * One uploaded file into other parts' specs, the bytes stored once (same storage key): each gets a spec if it has none,
   * a slot of the file's kind if it has none (named like the original's), and the file as that slot's newest upload,
   * unless it's already the current one. Returns { copied: [{ item_id, sku }], unchanged: [...], started: [...] }.
   */
  "spec_file.copy_to": custom(z.object({ file_version_id: z.uuid(), item_ids: z.array(z.uuid()).min(1, "Pick a part").max(500) }), STAFF),
} as const;
